import * as React from "react";
import { AxiosInstance } from "axios";
import { useState } from "react";
import {
  makeStyles,
  useId,
  Button,
  Input,
  Subtitle2,
  Title3,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { DismissCircle16Filled } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

/* global console */

interface NewProjectFormProps {
  axios: AxiosInstance;
  authHeader: string;
  onSubmitClick: () => {};
  onCancelClick: () => {};
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    // Prevent the example from taking the full width of the page (optional)
    maxWidth: "400px",
    // Stack the label above the field (with 2px gap per the design system)
    "> div": {
      display: "flex",
      flexDirection: "column",
      marginBottom: "20px",
    },
    paddingTop: "16px",
    paddingBottom: "16px",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "space-between",
  },
});

const NewProjectForm: React.FC<NewProjectFormProps> = ({
  axios,
  authHeader,
  onSubmitClick,
  onCancelClick,
}) => {
  const [id, setId] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null | undefined>(
    undefined
  );
  const [endDate, setEndDate] = useState<Date | null | undefined>(undefined);

  const { t } = useTranslation("main");
  const styles = useStyles();

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const projectsResponse = await axios.post(
        "/v1/microsoft_outlook/project",
        JSON.stringify({
          id: id,
          name: name,
          description: description,
          addresses: [
            {
              address: address,
              address_components: {
                postal_code: postalCode,
                street_address: address,
              },
            },
          ],
          start_date: startDate ? startDate.toISOString() : undefined,
          end_date: endDate ? endDate.toISOString() : undefined,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: authHeader,
          },
        }
      );
      console.log("Response: " + projectsResponse);
    } catch (error) {
      console.error(error);
    }
  };

  const projectIdId = useId("content-project-id");
  const projectNameId = useId("content-project-name");
  const projectAddressId = useId("content-project-address");
  const projectDescriptionId = useId("content-project-description");
  const projectStartDateId = useId("content-project-start-date");
  const projectEndDateId = useId("content-project-end-date");

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.header}>
        <Title3>{t("new_project")}</Title3>
        <Button
          appearance="transparent"
          size="small"
          onClick={onCancelClick}
          icon={<DismissCircle16Filled></DismissCircle16Filled>}
        ></Button>
      </div>
      <div className={styles.root}>
        <img src="assets/Benetics_Connect_infographic.png"></img>
        <div className={styles.root}>
          <Subtitle2>{t("new_project_warning")}</Subtitle2>
        </div>
        <div>
          <Input
            type="text"
            appearance="filled-darker"
            name="project_id"
            placeholder={t("new_project_project_id_placeholder")}
            value={id}
            id={projectIdId}
            onChange={(e) => {
              setId(e.target.value);
            }}
          />
        </div>
        <div>
          <Input
            type="text"
            appearance="filled-darker"
            name="project_name"
            placeholder={t("new_project_project_name_placeholder")}
            value={name}
            id={projectNameId}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </div>
        <div>
          <Input
            type="text"
            appearance="filled-darker"
            name="project_address"
            placeholder={t("new_project_address_placeholder")}
            value={address}
            id={projectAddressId}
            onChange={(e) => {
              const matches = e.target.value.match(new RegExp(",?([0-9]{4})"));
              if (matches !== null && matches !== undefined) {
                if (matches.length > 0) {
                  // matches[0] is the whole matched string. Take the first group,
                  // at index 1.
                  setPostalCode(matches[1]);
                }
              }
              setAddress(e.target.value);
            }}
          />
        </div>
        <div>
          <Input
            type="text"
            appearance="filled-darker"
            name="project_description"
            placeholder={t("new_project_description_placeholder")}
            value={description}
            id={projectDescriptionId}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
          />
        </div>
        <div>
          <DatePicker
            allowTextInput={true}
            type="text"
            appearance="filled-darker"
            name="project_start_date"
            placeholder={t("new_project_start_date_placeholder")}
            id={projectStartDateId}
            onSelectDate={setStartDate}
            showGoToToday={true}
          />
        </div>
        <div>
          <DatePicker
            allowTextInput={true}
            type="text"
            appearance="filled-darker"
            name="project_end_date"
            placeholder={t("new_project_end_date_placeholder")}
            id={projectEndDateId}
            onSelectDate={setEndDate}
            showGoToToday={true}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            justifyContent: "space-evenly",
            alignItems: "center",
            alignContent: "stretch",
          }}
        >
          <Button
            appearance="primary"
            size="medium"
            disabled={id === "" || name === "" || address === ""}
            onClick={(e) => {
              handleSubmit(e);
              onSubmitClick();
            }}
          >
            {t("new_project_create")}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default NewProjectForm;
